import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MdOutlineDashboard,
  MdAccountTree,
} from "react-icons/md";
import { FaUserGraduate, FaSignOutAlt, FaClipboardList } from "react-icons/fa";
import logo from "../Assets/dbskills-logo.webp";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const role = localStorage.getItem("role");
  const [activeButton, setActiveButton] = useState(
    localStorage.getItem("activeButton") || "Dashboard"
  );

  const menuItems = [
    {
      name: "Dashboard",
      href: "/dash",
      icon: <MdOutlineDashboard />,
      roles: ["admin", "branchUser"],
    },
    {
      name: "Centers",
      href: "/branches",
      icon: <MdAccountTree />,
      roles: ["admin"],
    },
    {
      name: "Candidates",
      href: "/candidates",
      icon: <FaUserGraduate />,
      roles: ["admin", "branchUser"],
    },
    {
      name: "Activity Logs",
      href: "/logs",
      icon: <FaClipboardList />,
      roles: ["admin"],
    },
  ];

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsSidebarOpen]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const currentItem = menuItems.find((item) => item.href === currentPath);
    if (currentItem) {
      setActiveButton(currentItem.name);
      localStorage.setItem("activeButton", currentItem.name);
    }
  }, [location.pathname]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

        .sidebar-wrap {
          background: linear-gradient(180deg, #f4fcfe 0%, #ffffff 60%, #edf9fc 100%);
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .nav-item-btn {
          display: flex;
          align-items: center;
          padding: 11px 14px;
          width: 100%;
          border-radius: 12px;
          gap: 12px;
          font-size: 14px;
          font-weight: 500;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: relative;
          overflow: hidden;
          text-align: left;
        }

        .nav-item-btn.inactive {
          background: transparent;
          color: #4b6472;
        }

        .nav-item-btn.inactive:hover {
          background: rgba(43,181,206,0.09);
          color: #2BB5CE;
          transform: translateX(3px);
        }

        .nav-item-btn.active-item {
          background: linear-gradient(135deg, #2BB5CE 0%, #1a9db8 100%);
          color: #ffffff;
          box-shadow: 0 4px 16px rgba(43,181,206,0.35);
          font-weight: 600;
        }

        .nav-item-btn.active-item::after {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 45%;
          background: linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 100%);
          pointer-events: none;
        }

        .nav-icon-wrap {
          font-size: 20px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .nav-item-btn:hover .nav-icon-wrap {
          transform: scale(1.12);
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 11px 14px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          border: none;
          cursor: pointer;
          background: transparent;
          color: #6b8590;
          transition: all 0.2s ease;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .logout-btn:hover {
          background: rgba(239,68,68,0.08);
          color: #ef4444;
          transform: translateX(3px);
        }

        .sidebar-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(43,181,206,0.25), transparent);
          margin: 10px 4px 12px;
        }

        .nav-stagger li { animation: navIn 0.3s ease both; }
        .nav-stagger li:nth-child(1) { animation-delay: 0.04s; }
        .nav-stagger li:nth-child(2) { animation-delay: 0.08s; }
        .nav-stagger li:nth-child(3) { animation-delay: 0.12s; }
        .nav-stagger li:nth-child(4) { animation-delay: 0.16s; }

        @keyframes navIn {
          from { opacity: 0; transform: translateX(-10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div
        className={`sidebar-wrap fixed top-0 left-0 z-50 h-screen overflow-y-auto shadow-2xl w-64
          transition-transform duration-300
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="p-6 flex justify-center border-b" style={{ borderColor: "rgba(43,181,206,0.15)" }}>
          <Link to="/" className="flex justify-center">
            <img src={logo} alt="Logo" className="w-28 h-auto" />
          </Link>
        </div>

        {/* Nav Items */}
        <ul className="px-4 mt-6 space-y-1 nav-stagger">
          {menuItems
            .filter((item) => item.roles.includes(role))
            .map((item) => {
              const isActive = activeButton === item.name;
              return (
                <li key={item.name}>
                  <button
                    className={`nav-item-btn ${isActive ? "active-item" : "inactive"}`}
                    onClick={() => {
                      setActiveButton(item.name);
                      localStorage.setItem("activeButton", item.name);
                      setIsSidebarOpen(false);
                      navigate(item.href);
                    }}
                  >
                    <span className="nav-icon-wrap">{item.icon}</span>
                    <span>{item.name}</span>
                  </button>
                </li>
              );
            })}

          {/* Divider before logout */}
          <div className="sidebar-divider" />

          {/* Logout */}
          <li>
            <button className="logout-btn" onClick={handleLogout}>
              <FaSignOutAlt style={{ fontSize: 18, flexShrink: 0 }} />
              <span>Logout</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Mobile overlay — identical to original */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 z-10 bg-black bg-opacity-40 lg:hidden"
        />
      )}
    </>
  );
};

export default Sidebar;